import React, { useMemo, useContext, createContext } from 'react'
import { Divider } from '@ui-kitten/components'
import { Parser } from 'commonmark'
import ReactRenderer from 'commonmark-react-renderer'
// The default styles applied by our base Text class override parent Text component styles;
// but, the markdown renderer relies on Text component style inheritance.
// eslint-disable-next-line max-len
import { Text as UnstyledText, TextStyle, View, StyleProp, ViewStyle } from 'react-native' // eslint-disable-line no-restricted-imports
import { StyleService, useStyleSheet } from '@src/style/service'
import { Icon, Text } from '@components/base'
import { openUrl } from '@src/utils/openUrl'
import { findTaskState } from './utils'

const parser = new Parser()

interface MarkdownViewProps {
  style?: StyleProp<ViewStyle>
  content: string
  paragraphStyle?: TextStyle
}

const LEVEL_TITLES: { [prop: number]: 'title-1' | 'title-2' | 'title-3' | 'large' | 'regular' } = {
  [1]: 'title-1',
  [2]: 'title-2',
  [3]: 'title-3',
  [4]: 'large',
  [5]: 'regular',
  [6]: 'regular',
}

const ListItemContext = createContext<{
  type: 'bullet' | 'ordered'
  index: number
  tight: boolean
}>({
  type: 'bullet',
  index: 0,
  tight: false,
})

export const MarkdownView = ({ content, paragraphStyle, style }: MarkdownViewProps) => {
  const styles = useStyleSheet(themedStyles)

  const renderer = useMemo(() => {
    // Defined separately so Item renderer can compare child.type against it
    // to detect block-level children (nested lists) that can't be inside Text
    const listRenderer = ({ children, start, type, tight }: any) => {
      const isOrdered = type === 'ordered'
      const items = React.Children.map(children, (child, idx) => (
        <ListItemContext.Provider
          key={idx}
          value={{
            type: isOrdered ? 'ordered' : 'bullet',
            index: isOrdered ? start + idx : 0,
            tight: !!tight,
          }}
        >
          {child}
        </ListItemContext.Provider>
      ))
      return <View style={styles.list}>{items}</View>
    }

    const codeBlockRenderer = ({ literal }: any) => (
      <Text selectable type="small" lineSpacing="tight" style={styles.codeBlock}>
        {literal}
      </Text>
    )

    const blockRenderers = new Set([listRenderer, codeBlockRenderer])

    return new ReactRenderer({
      renderers: {
        Linebreak: () => <Text type="regular">{'\n'}</Text>,
        List: listRenderer,
        Item: ({ children }) => {
          const { type, index, tight } = useContext(ListItemContext)
          const childArray = React.Children.toArray(children)

          const itemTaskState = findTaskState(children)
          const isTaskItem = itemTaskState !== null
          const isChecked = itemTaskState === 'checked'

          const hasBlockChild =
            tight &&
            childArray.some(
              (child) => React.isValidElement(child) && blockRenderers.has(child.type as any),
            )

          const shouldWrapInline = tight && !hasBlockChild
          const marker = type === 'ordered' ? `${index}. ` : '• '

          return (
            <View style={[styles.item, isTaskItem && styles.itemTask]}>
              {isTaskItem ? (
                <View style={[styles.checkbox, isChecked && styles.checkboxChecked]}>
                  {isChecked && <Icon name="check" style={styles.checkIcon} />}
                </View>
              ) : (
                <Text type={type === 'ordered' ? 'regular' : 'small'}>{marker}</Text>
              )}
              <View style={styles.itemContent}>
                {shouldWrapInline ? <Text type="regular">{children}</Text> : children}
              </View>
            </View>
          )
        },
        Link: ({ href, children }) => (
          <Text type="regular" bold style={styles.link} onPress={() => openUrl(href)}>
            {children}
          </Text>
        ),
        Blockquote: ({ children }) => <Text type="regular">{children}</Text>,
        Image: () => null,
        Code: ({ children }) => (
          <Text type="regular" style={styles.code}>
            {children}
          </Text>
        ),
        Emph: ({ children }) => (
          <Text type="regular" style={styles.emph}>
            {children}
          </Text>
        ),
        Paragraph: ({ children }) => (
          <Text type="regular" style={[styles.paragraph, paragraphStyle]}>
            {children}
          </Text>
        ),
        Strong: ({ children }) => (
          <Text type="regular" bold>
            {children}
          </Text>
        ),
        ThematicBreak: () => <Divider style={styles.divider} />,
        HtmlBlock: () => null,
        HtmlInline: () => null,
        CodeBlock: codeBlockRenderer,
        Heading: ({ level, children }) => (
          <Text type={LEVEL_TITLES[level]}>
            {children}
            {'\n'}
          </Text>
        ),
        Text: ({ literal }) => {
          const text = literal ?? ''
          // Strip sentinel characters — the Item renderer detects them from props
          if (text.startsWith('☐') || text.startsWith('☑')) {
            return <UnstyledText style={styles.text}>{text.slice(1)}</UnstyledText>
          }
          return <UnstyledText style={styles.text}>{text}</UnstyledText>
        },
        Softbreak: () => <Text type="regular">{'\n'}</Text>,
      },
    })
  }, [styles, paragraphStyle])

  const output = useMemo(() => {
    // Replace task list checkbox syntax with sentinel characters (☐/☑).
    // The Item renderer detects these in children's props.literal to decide
    // checkbox vs bullet. The Text renderer strips them from displayed text.
    const cleaned = content
      .replace(/^(\s*[-*])\s*\[[xX]]\s/gm, '$1 ☑')
      .replace(/^(\s*[-*])\s*\[ ]\s/gm, '$1 ☐')
    const abstractSyntaxTree = parser.parse(cleaned)
    return renderer.render(abstractSyntaxTree)
  }, [renderer, content])

  return <View style={style}>{output}</View>
}

const themedStyles = StyleService.create({
  link: {
    color: 'theme.text.link',
  },
  code: {
    fontFamily: 'NBInternationalProMono',
  },
  codeBlock: {
    color: 'theme.text.primary',
    fontFamily: 'NBInternationalProMono',
    marginBottom: 16,
  },
  paragraph: {
    marginBottom: 16,
  },
  emph: {
    fontStyle: 'italic',
  },
  list: {
    paddingLeft: 8,
    marginBottom: 8,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  itemContent: {
    flex: 1,
  },
  itemTask: {
    alignItems: 'center',
    marginTop: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: 'theme.text.tertiary',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: 'theme.primary.base',
    borderColor: 'theme.primary.base',
  },
  checkIcon: {
    width: 14,
    height: 14,
    color: 'theme.solid.white',
  },
  divider: {
    marginBottom: 12,
  },
  text: {
    color: 'theme.text.primary',
  },
})
